import { translateFn } from './translator.helper';
import { GzToast, ToastSeverity } from '../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../ui/gzToastNotification';

export interface ElementWithFullscreen extends HTMLElement {
  requestFullscreen(): Promise<void>;
  webkitRequestFullscreen(): Promise<void>;
}

export interface DocumentWithFullscreen extends Document {
  fullscreenElement;
  webkitFullscreenElement;
  exitFullscreen(): Promise<void>;
  webkitExitFullscreen(): Promise<void>;
}

export function addFullscreenEventListeners(
  element: HTMLElement | Window,
  listener: (this: HTMLElement, ev: Event) => unknown
) {
  element.addEventListener('fullscreenchange', listener);
  element.addEventListener('webkitfullscreenchange', listener);
}

export function removeFullscreenEventListeners(
  element: HTMLElement | Window,
  listener: (this: HTMLElement, ev: Event) => unknown
) {
  element.removeEventListener('fullscreenchange', listener);
  element.removeEventListener('webkitfullscreenchange', listener);
}

export function enterFullscreen(
  element: ElementWithFullscreen,
  onSuccess: () => void,
  onError: (error: unknown) => void
): void {
  let promise: Promise<void | undefined> = Promise.resolve(undefined);
  if (element.requestFullscreen) {
    promise = element.requestFullscreen();
  } else if (element.webkitRequestFullscreen) {
    promise = element.webkitRequestFullscreen();
  }
  promise?.then(onSuccess).catch(onError);
}

export function exitFullscreen(onSuccess: () => void, onError: (error: unknown) => void): void {
  const doc = document as DocumentWithFullscreen;
  let promise: Promise<void | undefined> = Promise.resolve(undefined);
  if (doc.exitFullscreen) {
    promise = doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    promise = doc.webkitExitFullscreen();
  }
  promise?.then(onSuccess).catch(onError);
}

export function onEnterFullscreenError(error: unknown): void {
  const displayMessage = translateFn('An error occurred while trying to switch into fullscreen mode');
  onFullscreenError(error, displayMessage);
}

export function onExitFullscreenError(error: unknown): void {
  const displayMessage = translateFn('An error occurred while trying to exit fullscreen mode');
  onFullscreenError(error, displayMessage);
}

export function onFullscreenError(error: unknown, displayMessage: string): void {
  if (!(error instanceof Error)) {
    return;
  }
  const toastNotificationConfigs: GzToast = {
    message: displayMessage,
    hasAction: false,
    severity: ToastSeverity.ERROR,
  };
  GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  throw new Error(displayMessage + `: ${error.message} (${error.name})`);
}

export function isBrowserFullscreen(): boolean {
  const doc = document as DocumentWithFullscreen;
  return !!(doc.fullscreenElement || doc.webkitFullscreenElement);
}
